import { createContext, FC, PropsWithChildren, useContext, useMemo } from 'react';

import {
  EntityType,
  PullRequest,
  usePr,
} from '@/hooks/visual-editor/descriptionChanges';
// import { useDbtCloudAccount } from './DbtCloudProvider';
import { useFeatureFlag } from './FeatureFlagProvider';
import { useResourceInfo } from './ResourceInfoProvider';

interface DescriptionChanges {
  pullRequest?: PullRequest;
  resourceIdentifiers?: string[];
  entityType: EntityType | undefined;
  featureEnabled: boolean;
  isLoading: boolean;
  dbtCredsError: boolean;
  gitCredsError: boolean;
  unsupportedGitProvider: boolean;
}

const DescriptionChangesContext = createContext<DescriptionChanges>({
  pullRequest: undefined,
  resourceIdentifiers: undefined,
  entityType: 'model',
  featureEnabled: false,
  isLoading: false,
  dbtCredsError: false,
  gitCredsError: false,
  unsupportedGitProvider: false,
});

export const useDescriptionChanges = () => {
  return useContext(DescriptionChangesContext);
};

export const DescriptionChangesProvider: FC<
  PropsWithChildren<{
    projectId?: number;
    entityType?: EntityType;
    resourceIdentifier?: string | null;
    isColumnLevel?: boolean;
  }>
> = ({
  children,
  projectId,
  resourceIdentifier,
  entityType,
  isColumnLevel = false,
}) => {
  const { resourceType, resourceIdentifiers: rootResourceIdentifiers } =
    useResourceInfo();

  // Do not use Account Features as source of truth until this is rolled out to GA
  // const { hasAccountFeature } = useDbtCloudAccount();
  // const enableExplorerContributions = hasAccountFeature(
  //   'explorerContributions',
  // );
  const enableExplorerContributions = useFeatureFlag(
    'meta4182ExplorerContributions',
    false,
  );

  const resIdentifiers = useMemo(() => {
    if (!rootResourceIdentifiers) return [];
    return resourceIdentifier
      ? [...rootResourceIdentifiers, resourceIdentifier]
      : rootResourceIdentifiers;
  }, [rootResourceIdentifiers, resourceIdentifier]);

  // TODO: pr checks are disabled for columns until META-4429 is done
  const shouldCheckPR = enableExplorerContributions && !isColumnLevel;

  // states:
  // 1. feature flag disabled -> (user can't do anything)
  // 2. feature flag enabled
  //    a. no patch path -> (user can't do anything)
  //    b. dbt credentials check fails (403) -> (user can't do anything)
  //    c. git credentials (404) -> (user can't do anything)
  //    d. git credentials and dbt credentials check passes
  //      i. description is inherited -> (user can't edit)
  //      ii. branch exists -> (user can't edit)
  //      iii. description is inherited -> (user can't edit)
  //      iv. branch doesn't exist (404) -> (user can edit)
  const {
    data: apiPrData,
    error: apiPrError,
    isLoading: apiPrLoading,
  } = usePr(projectId ?? 0, resIdentifiers, shouldCheckPR);

  const defaultPrData = {
    pullRequest: undefined,
    isLoading: false,
    dbtCredsError: false,
    gitCredsError: false,
    unsupportedGitProvider: false,
  };

  const prData = {
    pullRequest: apiPrData,
    isLoading: apiPrLoading,
    dbtCredsError: apiPrError?.response?.status === 403,
    gitCredsError:
      apiPrError?.response?.status === 404 &&
      (apiPrError?.response.data as any)?.detail === 'Git provider access denied',
    unsupportedGitProvider:
      apiPrError?.response?.status === 404 &&
      (apiPrError?.response.data as any)?.detail === 'Unsupported git provider',
  };

  const entity = useMemo(() => {
    if (!isColumnLevel) return entityType;
    if (resourceType === 'model') return 'model_column';
    if (resourceType === 'source') return 'source_table_column';
  }, [resourceType, entityType, isColumnLevel]);

  const descriptionChangeProps: DescriptionChanges = {
    resourceIdentifiers: resIdentifiers,
    entityType: entity,
    featureEnabled: enableExplorerContributions,
    ...(!isColumnLevel ? prData : defaultPrData),
  };

  return (
    <DescriptionChangesContext.Provider value={descriptionChangeProps}>
      {children}
    </DescriptionChangesContext.Provider>
  );
};
